import { useMemo, useState } from 'react';
import { useGetFollowedCoachesQuery } from 'types/generated/client';
import type { MarketplaceLinkParams } from 'utils/shared/url/marketplaceUrl';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import { neighborhoodCenter } from 'screens/Find/geo';
import { BookCoachModal } from 'screens/PlayerLoggedInScreen/components';
import LatestRecapCard from 'screens/PlayerLoggedInScreen/components/LatestRecapCard';
import { CoachType } from 'screens/PlayerLoggedInScreen/props';
import MainLayout from 'layouts/MainLayout';
import DiscoverSection from 'components/DiscoverSection';
import GeoLocationInitializer from 'components/utilities/GeoLocationInitializer';
import Head from 'components/utilities/Head';
import {
  BookableCourtsSection,
  GetOnCourtSection,
  HomeIntentEnum,
  ImproveYourGameSection,
  TopOpportunitiesSection,
  TopPlayerMatchesSection,
  useLatestRecap,
} from './components';
import { useHomeUrlFilters } from './useHomeUrlFilters';

const PlayerHomeV3 = () => {
  // The top bar lives in the URL so returning to Home restores what was chosen;
  // the rest of the Filters sheet is in-memory. Feed sections consume both.
  const { filters, changeFilters, sheetFilters, changeSheetFilters } = useHomeUrlFilters();

  // Leaving Home keeps the search: marketplaces read `sport` and seed their location
  // from lat/lng. Only a picked neighborhood travels — the marketplaces still centre
  // on the viewer without one, unlike Home and Find, which now default to New York.
  const [pickedNeighborhood] = sheetFilters.neighborhoods;
  const marketplaceParams = useMemo<MarketplaceLinkParams>(
    () => ({
      sport: filters.sport,
      center: pickedNeighborhood ? neighborhoodCenter(pickedNeighborhood) : null,
    }),
    [filters.sport, pickedNeighborhood],
  );

  // Book a court replaces the games feed with its own club/time feed, so the
  // sections that list games, courts and coaches would just repeat it.
  const isBookCourt = filters.intent === HomeIntentEnum.BookCourt;

  const viewer = useViewer();
  const { recap, dismissRecap } = useLatestRecap();

  // Booking flow reused from v2: the recap's "Book next lesson" CTA opens the
  // BookCoachModal for the followed coach matching the recap's coach.
  const { data: dataFollowedCoaches } = useGetFollowedCoachesQuery({
    variables: { userId: viewer.userId ?? '' },
    skip: !viewer.userId,
    fetchPolicy: 'cache-and-network',
  });
  const bookCoachModal = useModal();
  const [activeCoach, setActiveCoach] = useState<CoachType | null>(null);

  const handleBookLessonWithCoach = (coachId: string) => {
    const coach = dataFollowedCoaches?.userProfiles.find((coach) => coach.id === coachId);
    if (coach) {
      setActiveCoach(coach);
      bookCoachModal.openModal();
    }
  };

  return (
    <>
      <Head noIndex title="Player Home" description="Player Home Page" />
      <MainLayout
        withFooter
        mainClassName="relative flex flex-col gap-12 pb-20 pt-ds-lg max-sm:gap-ds-2xl"
      >
        <GeoLocationInitializer>
          {/* Floating new-recap reminder: on desktop it's lifted out of the flex flow
              (would otherwise eat a gap slot) and overlays the top-right, z-30 to clear
              the GetOnCourt bar; on mobile it flows above the sections. */}
          {recap && (
            <div className="z-30 sm:absolute sm:inset-x-0 sm:top-ds-lg">
              <div className="container relative mx-auto">
                <LatestRecapCard
                  recap={recap}
                  handleBookAnotherLesson={handleBookLessonWithCoach}
                  onDismiss={dismissRecap}
                />
              </div>
            </div>
          )}
          <GetOnCourtSection
            filters={filters}
            onChange={changeFilters}
            sheetFilters={sheetFilters}
            onSheetFiltersChange={changeSheetFilters}
          />
          <TopOpportunitiesSection
            filters={filters}
            onFiltersChange={changeFilters}
            sheetFilters={sheetFilters}
            onSheetFiltersChange={changeSheetFilters}
            marketplaceParams={marketplaceParams}
          />
          {!isBookCourt && (
            <>
              <TopPlayerMatchesSection filters={filters} sheetFilters={sheetFilters} />
              <BookableCourtsSection
                filters={filters}
                sheetFilters={sheetFilters}
                marketplaceParams={marketplaceParams}
              />
              <ImproveYourGameSection
                filters={filters}
                sheetFilters={sheetFilters}
                marketplaceParams={marketplaceParams}
              />
            </>
          )}
          <DiscoverSection
            title="Discover More"
            className="!mt-[0.875rem]"
            marketplaceParams={marketplaceParams}
          />
        </GeoLocationInitializer>
      </MainLayout>
      <BookCoachModal
        open={bookCoachModal.isOpen}
        handleClose={bookCoachModal.toggleModal}
        coach={activeCoach as CoachType}
        refetchUpcomingLessons={() => {}}
      />
    </>
  );
};

export default PlayerHomeV3;
